class FeaturedCollectionsCarousel extends HTMLElement {
  #controller;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.track = this.querySelector('.fcc__track');
    this.prevButton = this.querySelector('.fcc__arrow--prev');
    this.nextButton = this.querySelector('.fcc__arrow--next');
    this.progress = this.querySelector('.fcc__progress');

    if (!this.track) return;

    this.prevButton?.addEventListener('click', () => this.#scrollByCard(-1), { signal });
    this.nextButton?.addEventListener('click', () => this.#scrollByCard(1), { signal });
    this.track.addEventListener('scroll', () => this.#updateState(), { signal, passive: true });
    window.addEventListener('resize', () => this.#refresh(), { signal });

    this.#refresh();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  #refresh() {
    this.#checkOverflow();
    this.#buildDots();
    this.#updateState();
  }

  #checkOverflow() {
    const hasOverflow = this.track.scrollWidth > this.track.clientWidth + 1;
    this.classList.toggle('fcc__carousel--overflow', hasOverflow);
  }

  #scrollByCard(direction) {
    const card = this.track.querySelector('.fcc__card');
    if (!card) return;

    const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
    const distance = (card.getBoundingClientRect().width + gap) * direction;

    this.track.scrollBy({ left: distance, behavior: 'smooth' });
  }

  #buildDots() {
    if (!this.progress) return;

    const cards = this.track.querySelectorAll('.fcc__card');
    if (!cards.length) {
      this.pageDistance = 0;
      this.pageCount = 0;
      this.progress.replaceChildren();
      return;
    }

    const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
    const cardWidth = cards[0].getBoundingClientRect().width;
    const perPage = Math.max(1, Math.round((this.track.clientWidth + gap) / (cardWidth + gap)));

    this.pageDistance = perPage * (cardWidth + gap);
    this.pageCount = Math.ceil(cards.length / perPage);

    this.progress.replaceChildren();
    const { signal } = this.#controller;
    const labelTemplate = this.progress.dataset.goToPageLabel || 'Go to page __INDEX__';

    for (let page = 0; page < this.pageCount; page += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'dot';
      dot.setAttribute('aria-label', labelTemplate.replace('__INDEX__', page + 1));
      dot.addEventListener(
        'click',
        () => this.track.scrollTo({ left: page * this.pageDistance, behavior: 'smooth' }),
        { signal }
      );
      this.progress.appendChild(dot);
    }
  }

  #updateState() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const maxScroll = scrollWidth - clientWidth;

    if (this.prevButton) this.prevButton.style.display = scrollLeft <= 0 ? 'none' : '';
    if (this.nextButton) this.nextButton.style.display = scrollLeft >= maxScroll - 1 ? 'none' : '';

    if (this.progress && this.pageDistance) {
      const currentPage = Math.min(this.pageCount - 1, Math.round(scrollLeft / this.pageDistance));

      this.progress.querySelectorAll('.dot').forEach((dot, index) => {
        dot.classList.toggle('is-selected', index === currentPage);
      });
    }
  }
}

if (!customElements.get('fcc-carousel')) {
  customElements.define('fcc-carousel', FeaturedCollectionsCarousel);
}
